import { parsePlaylistUrl } from '@streaming/mouflon-utils';
import { IManifest, IRenditionReport } from 'm3u8-parser';

import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { Logger, TLogger } from '@logger/Logger';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { EPlaylistRotationControllerType, StreamLoader } from '@streamLoader/StreamLoader';
import { StreamLoaderModuleEvents } from '@streamLoader/StreamLoader.events';
import bind from '@utils/decorators/bind';

import { IResolution } from './IResolutionsManager';
import { IResolutionManagerConfig } from './ResolutionManager.config';
import { ResolutionsManagerEvents } from './ResolutionsManager.events';

export class ResolutionsManager extends ConfigConsumer<IResolutionManagerConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = { currentResolution: '' };
  public groups: string[] = ['resolutionsManager'];

  private _currentResolutionsDecreasing: IResolution[] = [];
  private _currentResolution: IResolution | null = null;
  private _awaitingResolution: IResolution | null = null;
  private _lastMSN: number = -1;
  private _lastDSN: number = -1;
  private _lastPDT: number = -1;
  private _url: string = '';
  private _isAutoEnabled: boolean = true;
  private _logger: TLogger;
  private _lastPlaylistRotatorType: EPlaylistRotationControllerType = EPlaylistRotationControllerType.SIMPLE;
  private _streamLoader: StreamLoader | null = null;
  private _restartTimeout: ReturnType<typeof globalThis.setTimeout> | null = null;
  private _renditionReports: IRenditionReport[] = [];
  private _lastLoadedFragment: Fragment | null = null;
  private readonly _multiVariantsPlaylistDetectionListener: string;
  private readonly _onResolutionChangeRequestedListener: string;
  private readonly _onPlaylistInfoAvailableListener: string;
  private readonly _onRestartFromMainRequestedListener: string;
  private readonly _onPlaylistRotatorTypeChangedListener: string;
  private readonly _onEndOfSegmentPlaylistLoadedListener: string;
  private readonly _onFragmentAddedListener: string;
  private _codecsByResolutions: Record<string, string> = {};

  constructor(eventBus: TEventBus) {
    super('resolutionsManager', eventBus);
    this._logger = Logger(eventBus, 'Doppio - ResolutionManager');

    this._multiVariantsPlaylistDetectionListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlistType.multivariantDetected,
      this._onMultiVariantsPlaylists,
    );

    this._onResolutionChangeRequestedListener = this._eventBus.subscribe(
      ResolutionsManagerEvents.requestResolutionChange,
      this._onResolutionChangeRequested,
    );

    this._onPlaylistInfoAvailableListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlists.playlistInfo,
      this._onPlaylistInfoAvailable,
    );

    this._onRestartFromMainRequestedListener = this._eventBus.subscribe(
      ResolutionsManagerEvents.requestRestartFromMain,
      this._restartFormMain,
    );

    this._onPlaylistRotatorTypeChangedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.streamLoader.playlistRotatorTypeChanged,
      this._onPlaylistRotatorTypeChanged,
    );

    this._onEndOfSegmentPlaylistLoadedListener = this._eventBus.subscribe(
      BasePlaylistRotationControllerEvents.playlists.lastPlaylistOfSegmentLoaded,
      this._onEndOfSegmentPlaylistLoaded,
    );

    this._onFragmentAddedListener = this._eventBus.subscribe(BufferControllerEvents.fragmentAdded, this._onFragmentAdded);
  }

  public destroy(): void {
    super.destroy();

    if (this._restartTimeout) {
      clearTimeout(this._restartTimeout);
      this._restartTimeout = null;
    }

    this._eventBus.unsubscribe(this._multiVariantsPlaylistDetectionListener);
    this._eventBus.unsubscribe(this._onResolutionChangeRequestedListener);
    this._eventBus.unsubscribe(this._onPlaylistInfoAvailableListener);
    this._eventBus.unsubscribe(this._onRestartFromMainRequestedListener);
    this._eventBus.unsubscribe(this._onPlaylistRotatorTypeChangedListener);
    this._eventBus.unsubscribe(this._onEndOfSegmentPlaylistLoadedListener);
    this._eventBus.unsubscribe(this._onFragmentAddedListener);

    this._streamLoader?.destroy();

    this._codecsByResolutions = {};
  }

  public setUrl(url: string): void {
    if (this._url === url) {
      return;
    }

    this._lastPDT = -1;
    this._lastDSN = -1;
    this._lastMSN = -1;

    this._logger.info('Setting new url');

    this._url = url;
    this._currentResolution = null;
    this._currentResolutionsDecreasing = [];

    this._restart();
  }

  public setResolution(resolution: IResolution, isAbrRequest: boolean = false): void {
    this._awaitingResolution = null;

    if (resolution.name === this._currentResolution?.name) {
      return;
    }

    // TODO: This workaround should fix cases when, for some reason, we try to set a resolution from a wrong stream
    // This might happen, for example, when users are watching one stream, and then are trying to go to the next stream
    // setUrl method is supposed to be used in that case, and at that moment we could encounter the mentioned problem
    // We actually should not parse playlistUrl, neither should we know what a playlistUrl consists of.
    // We should think of a better way to avoid setting wrong current resolution
    if (
      this._url &&
      parsePlaylistUrl(this._url).streamId &&
      parsePlaylistUrl(resolution.url).streamId &&
      parsePlaylistUrl(this._url).streamId !== parsePlaylistUrl(resolution.url).streamId
    ) {
      this._logger.warn(
        `Tried to set resolution with ${resolution.url}, but the current url is ${this._url}. Skipping since they don't match`,
      );

      return;
    }

    const prevResolution: IResolution | null = this._currentResolution;

    this._logger.info(`Resolution changed: ${this._currentResolution?.name} -> ${resolution.name}`);

    this._currentResolution = resolution;
    this.additionalInfo.currentResolution = resolution.name;

    this._eventBus.publish(ResolutionsManagerEvents.resolutionChanged, resolution);

    if (this._codecsByResolutions[resolution.url]) {
      this._eventBus.publish(BufferControllerEvents.changeCodec, { codec: this._codecsByResolutions[resolution.url] });
    }

    if (isAbrRequest || !prevResolution) {
      this._eventBus.publish(ResolutionsManagerEvents.resolutionAutoChanged, {
        prevResolution: prevResolution?.name,
        nextResolution: resolution.name,
      });
    }

    this._restart();
  }

  public setNearestResolutionByWidth(expectedWidth: number): void {
    return this._setResolutionByCriteria(({ width }) => width <= expectedWidth);
  }

  public setNearestResolutionByHeight(expectedHeight: number): void {
    return this._setResolutionByCriteria(({ height }) => height <= expectedHeight);
  }

  public setNearestResolutionByBitrate(expectedBitrate: number): void {
    return this._setResolutionByCriteria(({ bitrate }) => bitrate <= expectedBitrate + this.config.initialResolutionBitrateThreshold);
  }

  public setResolutionByName(requestedName: string): void {
    return this._setResolutionByCriteria(({ name }) => name === requestedName);
  }

  public toggleAutoResolution(isAutoEnabled: boolean): void {
    this._isAutoEnabled = isAutoEnabled;
  }

  public restart(): void {
    this._restart();
  }

  @bind
  public _handleConfigUpdated(): void {}

  private _setResolutionByCriteria(criteria: (resolution: IResolution) => boolean): void {
    const resolution: IResolution | null = this._findResolutionByCriteria(criteria);

    if (!resolution) {
      this._logger.warn('There are no any resolution in current stream');
      this.state = EDebuggableModuleState.ERROR;
      this._awaitingResolution = null;

      return;
    }

    if (this.config.awaitForSegmentEndToSwitchManual && this._currentResolution && this._lastMSN > -1) {
      this._logger.info('Await for segment end for switch resolution');
      this._awaitingResolution = resolution;

      return;
    }

    this.setResolution(resolution);
  }

  private _findResolutionByCriteria(criteria: (resolution: IResolution) => boolean): IResolution | null {
    if (!this._currentResolutionsDecreasing.length) {
      return null;
    }

    if (this._currentResolutionsDecreasing.length === 1) {
      return this._currentResolutionsDecreasing[0];
    }

    const bestMatchedResolution: IResolution | undefined = this._currentResolutionsDecreasing.find(criteria);
    if (!bestMatchedResolution) {
      this._eventBus.publish(ResolutionsManagerEvents.resolutionNotFound, {});

      if (this.config.useFallbackResolutionIfResolutionNotFound === false) {
        return null;
      }
    }
    return bestMatchedResolution || this._currentResolutionsDecreasing.slice(-1)[0];
  }

  @bind
  private _restartFormMain(): void {
    this._currentResolution = null;
    this._logger.info('Restart from main');
    this._renditionReports = [];
    this._eventBus.publish(BufferControllerEvents.requestResetWithOverplay, {});
    this._restart(2000);
  }

  private _restart(restartTimeout: number = 0): void {
    if (this._restartTimeout) {
      clearTimeout(this._restartTimeout);
      this._restartTimeout = null;
    }

    this._logger.info('Restart resolution manager');

    this._destroyStreamLoader();

    this._restartTimeout = setTimeout(() => {
      this._recreateStreamLoader();
    }, restartTimeout);
  }

  private _destroyStreamLoader(): void {
    this._streamLoader?.destroy();
    this._streamLoader = null;
    this.status = 'Awaiting resolutions';
  }

  private _recreateStreamLoader(): void {
    this._destroyStreamLoader();
    this._streamLoader = new StreamLoader(this._lastPlaylistRotatorType, this._lastLoadedFragment, this._eventBus);
    this._streamLoader.setActualRenditionReport(this._getActualRenditionReport());
    this._streamLoader.startLoad(this._currentResolution ? this._currentResolution.url : this._url);
  }

  @bind
  private _onMultiVariantsPlaylists(resolutions: Record<string, IResolution>): void {
    this._currentResolutionsDecreasing = Object.values(resolutions).sort((a, b) => b.bitrate - a.bitrate);

    this.additionalInfo.resolutionsAvailable = this._currentResolutionsDecreasing.map(({ name }) => name);

    this._eventBus.publish(ResolutionsManagerEvents.resolutionsAvailable, this._currentResolutionsDecreasing);

    if (this.config.initialResolutionBitrate) {
      this.setNearestResolutionByBitrate(this.config.initialResolutionBitrate);

      return;
    }

    if (this.config.initialResolutionHeight) {
      this.setNearestResolutionByHeight(this.config.initialResolutionHeight);

      return;
    }

    if (this.config.initialResolutionWidth) {
      this.setNearestResolutionByWidth(this.config.initialResolutionWidth);

      return;
    }

    if (this.config.autoloadLowestResolution) {
      this.setResolution(this._currentResolutionsDecreasing.slice(-1)[0]);
    }
  }

  @bind
  private _onEndOfSegmentPlaylistLoaded(): void {
    if (this._awaitingResolution) {
      this._logger.info('Last playlist of segment loaded. Ready to switch');

      this.setResolution(this._awaitingResolution, true);
    }
  }

  @bind
  private _onFragmentAdded(fragment: Fragment): void {
    this._lastLoadedFragment = fragment;
  }

  @bind
  private _onPlaylistInfoAvailable(playlistInfo: IManifest): void {
    const { mediaSequence, discontinuitySequence, segments } = playlistInfo;

    if (playlistInfo.playlists) {
      this._codecsByResolutions = playlistInfo.playlists.reduce<Record<string, string>>((acc, curr) => {
        if (!curr.attributes?.CODECS) {
          return acc;
        }

        return {
          ...acc,
          [curr.uri]: curr.attributes.CODECS,
        };
      }, {});
    }

    if (mediaSequence === undefined || discontinuitySequence === undefined || segments === undefined) {
      return;
    }

    const firstSegmentPDT: number = segments[0]?.dateTimeObject?.getTime() || 0;

    const isDataUnknown: boolean = this._lastPDT === -1 || this._lastDSN === -1 || this._lastMSN === -1;

    if (!isDataUnknown) {
      if (discontinuitySequence !== this._lastDSN) {
        this._logger.info('DSN changed. Requesting overplay');
        this._eventBus.publish(BufferControllerEvents.requestResetWithOverplay, {});
      }

      if (Math.abs(firstSegmentPDT - this._lastPDT) > this.config.maxPlaylistTimeDifference) {
        this._logger.warn('PDT delta reached max delta. Restarting from main');
        this._restartFormMain();
      }
    }

    this._lastMSN = mediaSequence;
    this._lastDSN = discontinuitySequence;
    this._lastPDT = firstSegmentPDT;

    this._renditionReports = playlistInfo.renditionReports || [];

    this._streamLoader?.setActualRenditionReport(this._getActualRenditionReport());
  }

  @bind
  private _onResolutionChangeRequested(resolution: IResolution): void {
    if (!this._isAutoEnabled) {
      return;
    }

    if (this.config.awaitForSegmentEndToSwitch) {
      this._logger.info('Await for segment end for switch resolution');
      this._awaitingResolution = resolution;

      return;
    }

    this.setResolution(resolution, true);
  }

  private _getActualRenditionReport(): IRenditionReport | null {
    if (!this._renditionReports.length) {
      return null;
    }

    try {
      const renditionReport: IRenditionReport | undefined = this._renditionReports.find(
        ({ uri }) => new URL(uri).pathname === new URL(this._currentResolution?.url || '').pathname,
      );

      return renditionReport || null;
    } catch (e) {
      return null;
    }
  }

  @bind
  private _onPlaylistRotatorTypeChanged(rotatorType: EPlaylistRotationControllerType): void {
    this._lastPlaylistRotatorType = rotatorType;
  }

  get additionalModules(): Record<string, IDebbugable> {
    this.additionalInfo.isAutoEnabled = this._isAutoEnabled;
    return !this._streamLoader ? {} : { streamLoader: this._streamLoader };
  }
}
